const sections = [
  {
    title: "1. Overview",
    body: [
      "This Privacy Policy explains how iBox collects, uses, stores, shares, and protects information when you use the iBox website, desktop application, downloadable software, documentation, and related services.",
      "By using iBox, you agree to the collection and use of information described in this Privacy Policy.",
    ],
  },
  {
    title: "2. Information we collect",
    body: [
      "Account information: We collect information you provide when creating or managing an account, such as your email address, one-way password hash, verification status, and account preferences.",
      "Billing information: Paid subscriptions are processed through our payment provider. We may receive and store limited billing-related information such as subscription status, plan type, customer identifiers, invoice status, and payment provider IDs. We do not store full payment card numbers.",
      "Contact information: If you contact us, we collect the information you provide, such as your name, email address, topic, message, and related support or correspondence details.",
      "App configuration information: iBox may store settings, presets, module configurations, ticker layouts, app preferences, and related configuration data needed to provide the features you choose to use.",
      "Technical information: We may collect technical information such as IP address, device type, operating system, app version, browser type, log data, crash information, timestamps, and diagnostic information needed for security, troubleshooting, and product reliability.",
    ],
  },
  {
    title: "3. Local files, logos, images, and overlay assets",
    body: [
      "iBox is designed so user-provided overlay assets, including logos, images, graphics, textures, and other local media, are primarily stored and processed locally on your device unless you choose a feature that sends or backs up that data elsewhere.",
      "When you use local asset tools, iBox may process assets on your device to crop, resize, normalize, recolor, remove solid backgrounds, generate previews, or display them in your configured output.",
      "You are responsible for the content you add to iBox and for ensuring you have the rights needed to use that content in your broadcasts, streams, overlays, and related materials.",
    ],
  },
  {
    title: "4. Information from integrations",
    body: [
      "iBox may connect to third-party platforms, APIs, feeds, or services that you configure, such as motorsport data sources, RSS feeds, streaming platforms, sim-racing services, payment providers, or other integrations.",
      "Information received from integrations depends on the integration, your configuration, and the permissions or public data made available by that third party. Examples may include public profile information, stream information, social handles, RSS headlines, racing statistics, league information, or related display data.",
      "Third-party services have their own privacy policies and practices. iBox is not responsible for the privacy practices of third-party services.",
    ],
  },
  {
    title: "5. How we use information",
    body: [
      "We use information to provide, operate, maintain, and improve iBox; create and manage accounts; process subscriptions; respond to support and contact requests; save preferences; display configured broadcast graphics; provide documentation; troubleshoot issues; prevent abuse; secure the service; and comply with legal obligations.",
      "We may also use aggregated or de-identified information to understand product usage, diagnose reliability issues, and improve iBox without identifying individual users.",
    ],
  },
  {
    title: "6. How we share information",
    body: [
      "We do not sell your personal information.",
      "We may share information with service providers that help us operate iBox, such as hosting providers, payment processors, email providers, analytics or diagnostics providers, and other vendors that process information on our behalf.",
      "We may share information when required by law, legal process, or governmental request; to protect the rights, safety, and security of iBox, users, or others; to prevent fraud or abuse; or in connection with a merger, acquisition, financing, reorganization, or sale of assets.",
    ],
  },
  {
    title: "7. Cookies and similar technologies",
    body: [
      "The iBox website and related services may use cookies, local storage, session storage, and similar technologies to keep you signed in, remember preferences, operate the website, improve security, and understand basic usage.",
      "Third-party providers, such as payment processors or embedded services, may use their own cookies or similar technologies according to their own policies.",
      "You can control cookies through your browser settings, but disabling cookies may affect account, checkout, preference, or website functionality.",
    ],
  },
  {
    title: "8. Data retention",
    body: [
      "We keep personal information for as long as reasonably necessary to provide iBox, maintain your account, process subscriptions, resolve disputes, enforce agreements, comply with legal obligations, and support legitimate business purposes.",
      "Local files and local app data may remain on your device until you delete them, uninstall iBox, reset local data, or otherwise remove them through your operating system or iBox settings.",
    ],
  },
  {
    title: "9. Security",
    body: [
      "We use reasonable administrative, technical, and organizational measures designed to protect information from unauthorized access, loss, misuse, alteration, or disclosure.",
      "No method of transmission or storage is completely secure. You are responsible for keeping your account credentials secure and for maintaining backups of important local configurations and assets.",
    ],
  },
  {
    title: "10. Your choices and rights",
    body: [
      "You may update certain account information through iBox or by contacting us. You may also request access, correction, deletion, or a copy of personal information associated with your account, subject to verification and legal limitations.",
      "Depending on where you live, you may have additional privacy rights, including rights to know, access, correct, delete, or limit certain uses of personal information.",
      "Because we do not sell personal information, we do not provide a separate opt-out for sale of personal information. If our practices change, we will update this Privacy Policy and provide any required choices.",
    ],
  },
  {
    title: "11. California privacy rights",
    body: [
      "California residents may have rights under California privacy laws, including the right to know what personal information is collected, used, disclosed, sold, or shared; the right to request deletion; the right to correct inaccurate personal information; the right to opt out of certain sales or sharing; and the right not to be discriminated against for exercising privacy rights.",
      "iBox does not sell personal information. If you would like to make a California privacy request, contact us through the contact page on the iBox website.",
    ],
  },
  {
    title: "12. Children's privacy",
    body: [
      "iBox is not intended for children under 18. We do not knowingly collect personal information from children. If you believe a child has provided personal information to iBox, contact us so we can review and delete it where appropriate.",
    ],
  },
  {
    title: "13. International users",
    body: [
      "iBox is operated from the United States. If you use iBox from outside the United States, you understand that information may be processed and stored in the United States or other locations where our service providers operate.",
    ],
  },
  {
    title: "14. Changes to this Privacy Policy",
    body: [
      "We may update this Privacy Policy from time to time. When we do, we will update the \"Last updated\" date. Continued use of iBox after an updated Privacy Policy becomes effective means you acknowledge the updated policy.",
    ],
  },
  {
    title: "15. Contact",
    body: [
      "Questions or requests about this Privacy Policy can be sent through the contact page on the iBox website.",
    ],
  },
];

export function PrivacyPage() {
  return (
    <main className="relative min-h-screen overflow-hidden pt-16">
      <div className="download-bg absolute inset-0 z-0 bg-bg">
        <div className="absolute inset-0 bg-gt-grid opacity-35" />
        <div className="absolute inset-0 bg-bg/40" />
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-64 bg-gradient-to-b from-transparent to-bg" />
      </div>

      <section className="relative z-10 mx-auto max-w-4xl px-6 py-24">
        <div className="mb-12">
          <div className="mb-6 flex items-center gap-3">
            <div className="h-px w-10 bg-primary" />
            <span className="text-[11px] font-semibold uppercase tracking-[0.3em] text-primary">Legal</span>
          </div>
          <h1 className="font-display text-6xl font-bold uppercase leading-[0.9] text-text md:text-8xl">Privacy Policy</h1>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-muted md:text-lg">
            Last updated: May 26, 2026
          </p>
        </div>

        <div className="rounded-xl border border-border bg-surface/92 shadow-2xl shadow-black/20">
          <div className="border-b border-border p-6 md:p-8">
            <p className="text-base leading-relaxed text-muted">
              This policy describes what information iBox collects, how it is used, and the choices you have.
            </p>
          </div>
          <div className="divide-y divide-border">
            {sections.map((section) => (
              <section key={section.title} className="p-6 md:p-8">
                <h2 className="font-display text-2xl font-bold uppercase leading-none text-text md:text-3xl">{section.title}</h2>
                <div className="mt-4 space-y-4">
                  {section.body.map((paragraph) => (
                    <p key={paragraph} className="text-sm leading-7 text-muted md:text-base">
                      {paragraph}
                    </p>
                  ))}
                </div>
              </section>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
